import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from 'antd';
import { DashboardOutlined, CalendarOutlined } from '@ant-design/icons';
import { MdTrendingDown, MdTrendingUp, MdRemove } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import type { SimilarListing } from '../hooks/useListingDetails';

interface SimilarVehiclesProps {
  listings: SimilarListing[];
  loading: boolean;
}

const PLACEHOLDER = 'https://images.unsplash.com/photo-1549399542-7e3f8b79c341?q=80&w=800&auto=format&fit=crop';

const buildPriceTagConfig = (t: (k: string) => string) => ({
  GOOD_PRICE: { bg: '#F3F4F6', color: '#059669', label: t('listing_details.good_price'), icon: <MdTrendingDown size={13} /> },
  FAIR_PRICE: { bg: '#F3F4F6', color: '#6B7280', label: t('listing_details.fair_price'), icon: <MdRemove size={13} /> },
  HIGH_PRICE: { bg: '#F3F4F6', color: '#DC2626', label: t('listing_details.high_price'), icon: <MdTrendingUp size={13} /> },
});

const SimilarVehicles: React.FC<SimilarVehiclesProps> = ({ listings, loading }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const priceTagConfig = buildPriceTagConfig(t);

  if (!loading && listings.length === 0) return null;

  const handleClick = (listing: SimilarListing) => {
    const target = listing.slug ? `/listing/${listing.slug}` : `/listing/${listing.id}`;
    navigate(target);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <section className="similar-section">
      <div className="similar-header">
        <h2 className="section-title-hp">{t('similar_vehicles.title')}</h2>
        <p className="similar-subtitle">{t('similar_vehicles.subtitle')}</p>
      </div>

      <div className="similar-grid">
        {loading
          ? Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="similar-card skeleton-card">
                <Skeleton.Image style={{ width: '100%', height: 180, borderRadius: '12px 12px 0 0' }} active />
                <div style={{ padding: '16px' }}>
                  <Skeleton active paragraph={{ rows: 2 }} title={false} />
                </div>
              </div>
            ))
          : listings.map((listing) => {
              const tag = listing.priceTag ? priceTagConfig[listing.priceTag] : null;
              return (
                <div
                  key={listing.id}
                  className="similar-card"
                  onClick={() => handleClick(listing)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleClick(listing)}
                >
                  <div className="similar-card-img-wrapper">
                    <img
                      src={listing.image || PLACEHOLDER}
                      alt={listing.title}
                      loading="lazy"
                      className="similar-card-img"
                      onError={(e) => { (e.target as HTMLImageElement).src = PLACEHOLDER; }}
                    />
                    {tag && (
                      <span
                        className="similar-card-tag"
                        style={{ background: tag.bg, color: tag.color, display: 'inline-flex', alignItems: 'center', gap: 3 }}
                      >
                        {tag.icon}{tag.label}
                      </span>
                    )}
                  </div>
                  <div className="similar-card-body">
                    <h3 className="similar-card-title">{listing.title}</h3>
                    <div className="similar-card-meta">
                      {listing.year && (
                        <span>
                          <CalendarOutlined /> {listing.year}
                        </span>
                      )}
                      {listing.mileage != null && (
                        <span>
                          <DashboardOutlined /> {listing.mileage.toLocaleString()} km
                        </span>
                      )}
                    </div>
                    <div className="similar-card-price">
                      {listing.price != null
                        ? `€ ${listing.price.toLocaleString('de-DE')}`
                        : 'Price on request'}
                    </div>
                  </div>
                </div>
              );
            })}
      </div>
    </section>
  );
};

export default SimilarVehicles;
